<template>
  <button class="button" :class="buttonClassList" :type="type" :style="height">
    <div class="button__content">
      <span class="button__slot">
        <slot></slot>
      </span>
      <div v-if="loading" class="button__spinner_holder">
        <LoaderCircular :height="24" class="button__spinner" />
      </div>
    </div>
  </button>
</template>

<script>
import BaseComponent from "../BaseComponent";
import InteractiveElement from "../mixins/InteractiveElements";
import ComponentIcons from "../mixins/ComponentIcons";
import LoaderCircular from "./LoaderCircular.vue";

/**
 * @group Buttons
 */
export default {
  name: "Button",
  mixins: [BaseComponent, InteractiveElement, ComponentIcons],
  components: {
    LoaderCircular,
  },
  props: {
    // Defines the button color
    buttonStyle: {
      // `'primary'` / `'secondary'` / `'negative'`
      type: String,
      default: "primary",
      validator(value) {
        if (["primary", "secondary", "negative"].includes(value)) {
          return true;
        }
        // Blue and White are deprecated. Button Consumer should use Primary and Secondary instead.
        if (["blue", "white"].includes(value)) {
          console.warn(
            'Deprecation warning: "blue" and "white" are deprecated as Button props. Use "primary" or "secondary" instead.'
          );
          return true;
        }
        console.error(
          'Custom Error: Expected buttonStyle to be one of "primary", "secondary" or "negative"'
        );
        return false;
      },
    },
    // Defines the html type of the button (i.e. submit or button)
    type: {
      type: String,
      default: "button",
    },
    // Overwrite the default height
    buttonHeight: {
      type: Number,
      default: 48,
    },
    // If this is true, button will disable pointer events and show a spinner.
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    height() {
      return {
        height: `${this.buttonHeight}px`,
      };
    },
    buttonStyleClass() {
      let { buttonStyle } = this;
      if (buttonStyle === "blue") buttonStyle = "primary";
      if (buttonStyle === "white") buttonStyle = "secondary";
      return `button--${buttonStyle}`;
    },
    buttonClassList() {
      const { loading, disabled, buttonStyleClass } = this;
      const buttonClassList = {
        "button--loading": loading,
        "button--disabled": disabled === true,
      };
      buttonClassList[buttonStyleClass] = true;
      return buttonClassList;
    },
  },
};

// Documentation Prop Exports
const documentationProps = {
  buttonStyle: "primary",
  type: "submit",
  loading: false,
  disabled: false,
};

export { documentationProps };
</script>

<style scoped lang="scss">
@import "../../style_variables/style_variables.scss";

// "name", background, border, font-color
$button_types: "primary" $blue $blue $white, "negative" $red $red $white;

// Button State Styling Return Function
@mixin getButtonStateStyle($background, $border, $font) {
  background: $background;
  border: 1px solid $border;
  color: $font;
}

.button {
  @extend %button_typography_style;
  position: relative;
  display: flex;
  justify-content: center;
  flex-grow: 0;
  flex-shrink: 0;
  align-items: center;
  border: 0px;
  border-radius: 4px;
  min-height: 32px;
  padding: 0px 24px;
  z-index: 2;
  white-space: nowrap;
  transition: all 100ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover,
  &:focus {
    cursor: pointer;
  }
  &:active {
    cursor: pointer;
    transition: all 36ms cubic-beziers(0.4, 0, 0.2, 1);
  }
  // States (except secondary, because there is an exception to this logic in the design)
  @each $name, $background, $border, $font in $button_types {
    &.button--#{$name} {
      @include getButtonStateStyle($background, $border, $font);
      &:hover {
        @include getButtonStateStyle(
          mix($background, #000, 80%),
          mix($border, #000, 80%),
          $font
        );
      }
      &:active {
        @include getButtonStateStyle(
          mix($background, #000, 60%),
          mix($border, #000, 60%),
          $font
        );
      }
    }
  }
  // Secondary state (defined seperately because of logic exception in design)
  &.button--secondary {
    @include getButtonStateStyle($white, $grey_athens, $grey_mineshaft);
    &:hover {
      @include getButtonStateStyle(
        $grey_alabaster,
        $grey_athens,
        $grey_mineshaft
      );
    }
    &:active {
      @include getButtonStateStyle(
        $grey_mischka,
        $grey_french,
        $grey_mineshaft
      );
    }
  }
  // Loading
  &.button--loading {
    @include getButtonStateStyle($grey_alabaster, $grey_alabaster, $white);
    pointer-events: none;
    .button__slot {
      opacity: 0;
    }
  }
  // Disabled
  &.button--disabled {
    @include getButtonStateStyle($grey_french, $grey_french, $white);
    pointer-events: none;
  }
}
.button__content {
  display: flex;
}
.button__slot {
  display: flex;
}
.button__spinner_holder {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  top: 0px;
  left: 0px;
}
.button__spinner {
  position: absolute;
}
</style>
